// EXTERNAL
import { useState } from "react";
// COMPONENTS
import * as ComponentLibrary from "@picnicmedia/masanatemples123";
// TYPES
import { CustomPackageState } from "../types";
import { FileKind } from "../functions/inspectPackage";

interface Props {
  pkg: CustomPackageState | null;
  busy?: boolean;
  saved?: boolean;
  onSelectFile: (file: File) => void;
  onClear: () => void;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

const KIND_ICON: Record<FileKind, string> = {
  html: "file-code",
  js: "file-code",
  css: "file-code",
  json: "file-code",
  img: "image",
  font: "font",
  other: "file",
};

/**
 * JH (30-07-26)
 * The "Custom" build-method inspector: drop a package .zip, see instant client-side validation, the
 * file card (replace / download / delete), read-only pixel dimensions, and the package contents. Save
 * lives in the top bar (see Creative.tsx) — this panel only stages + describes the package.
 */
export function CustomPackagePanel({ pkg, busy, saved, onSelectFile, onClear }: Props) {
  const [contentsOpen, setContentsOpen] = useState(true);
  const [dragging, setDragging] = useState(false);

  const pickFile = (files: FileList | null) => {
    const file = files && files[0];
    if (file) onSelectFile(file);
  };

  const downloadStaged = () => {
    if (!pkg) return;
    const url = URL.createObjectURL(pkg.file);
    const a = document.createElement("a");
    a.href = url;
    a.download = pkg.file.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const inspection = pkg?.inspection;

  return (
    <ComponentLibrary.PCContainerMain borderRadiusDirection={"top"}>
      <ComponentLibrary.PCSpacer type={"padding"} position={"around"} size={4}>
        <div className="platform-font">
          <div className="flex items-baseline">
            <ComponentLibrary.PCHeader text={"Custom"} />
            <span className="ml3 f6 mid-gray">HTML5, .zip up to 5MB</span>
          </div>
          <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />

          {/* Validation banner */}
          {busy && (
            <div className="pa3 br2 f6 platform-font flex items-center" style={{ backgroundColor: "#f2f2f2", color: "#555" }}>
              <ComponentLibrary.PCIconPicker icon={"spinner"} /> <span className="ml2">Checking file…</span>
            </div>
          )}
          {!busy && inspection && inspection.valid && (
            <div className="pa3 br2 f6 fw5 platform-font flex items-center" style={{ backgroundColor: "#eaf7ee", color: "#1e7e45" }}>
              <ComponentLibrary.PCIconPicker icon={"circle-check"} />
              <span className="ml2">
                Valid — {inspection.files.length} files, {formatBytes(inspection.zipBytes)}
                {inspection.width && inspection.height ? `, ${inspection.width} × ${inspection.height}` : ""}
                {inspection.clickTagCount ? `, ${inspection.clickTagCount} click tag${inspection.clickTagCount > 1 ? "s" : ""} detected` : ""}
                .
              </span>
            </div>
          )}
          {!busy && inspection && !inspection.valid && (
            <div className="pa3 br2 platform-font" style={{ backgroundColor: "#fdecec" }}>
              <div className="f6 fw6 flex items-center" style={{ color: "#c0392b" }}>
                <ComponentLibrary.PCIconPicker icon={"circle-exclamation"} />
                <span className="ml2">Not valid:</span>
              </div>
              <ul className="f6 mt2 mb0" style={{ color: "#c0392b", paddingLeft: "18px" }}>
                {inspection.problems.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </div>
          )}
          {!busy && inspection && inspection.valid && inspection.warnings.length > 0 && (
            <>
              <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={2} />
              <ul className="f7 mt0 mb0" style={{ color: "#8a6d1b", paddingLeft: "18px" }}>
                {inspection.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </>
          )}

          <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />

          {/* Drop zone (empty) or file card (staged) */}
          {!pkg ? (
            <label
              htmlFor={"custom-package-zip"}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                pickFile(e.dataTransfer.files);
              }}
              className={"db br3 tc pointer platform-font"}
              style={{ padding: "28px", border: `2px dashed ${dragging ? "#d6336c" : "#d9d9d9"}`, backgroundColor: dragging ? "#fdf0f5" : "#fafafa", transition: "background-color .15s, border-color .15s" }}>
              <input id={"custom-package-zip"} type={"file"} accept={".zip,application/zip"} style={{ display: "none" }} onChange={(e) => pickFile(e.target.files)} />
              <div className="mid-gray f6">
                Drag &amp; drop your <strong>.zip</strong> here, or
              </div>
              <div className="mt2">
                <span className="dib background-color-primary-3 white ph3 pv2 br2 fw6 f6">Browse</span>
              </div>
            </label>
          ) : (
            <div className="flex items-center justify-between pa3 br3" style={{ border: "1px solid #e3e3e3" }}>
              <div className="flex items-center">
                <div className="flex items-center justify-center br2" style={{ width: "40px", height: "40px", backgroundColor: "#f2f2f2" }}>
                  <ComponentLibrary.PCIconPicker icon={"file-zipper"} />
                </div>
                <div className="ml3">
                  <div className="fw6 f5 dark-gray">{pkg.file.name}</div>
                  <div className="f7 mid-gray">{formatBytes(pkg.file.size)} · {saved ? "saved" : "staged, not yet uploaded"}</div>
                </div>
              </div>
              <div className="flex items-center">
                <label htmlFor={"custom-package-replace"} className="pointer">
                  <input id={"custom-package-replace"} type={"file"} accept={".zip,application/zip"} style={{ display: "none" }} onChange={(e) => pickFile(e.target.files)} />
                  <span className="dib ph3 pv2 br2 f6 fw5 dark-gray" style={{ border: "1px solid #e3e3e3" }}>
                    Replace
                  </span>
                </label>
                <ComponentLibrary.PCSpacer type={"margin"} position={"left"} size={2} />
                <ComponentLibrary.PCIconButton icon={"download"} title={"Download"} clickHandler={downloadStaged} utilityClasses={"b--black-20"} inlineStyles={{ height: "36px", width: "36px" }} />
                <ComponentLibrary.PCSpacer type={"margin"} position={"left"} size={2} />
                <ComponentLibrary.PCIconButton icon={"trash"} title={"Remove"} clickHandler={onClear} utilityClasses={"b--black-20"} inlineStyles={{ height: "36px", width: "36px" }} />
              </div>
            </div>
          )}

          {/* Pixel dimensions (read-only) + package contents */}
          {pkg && inspection && (
            <>
              {inspection.width && inspection.height && (
                <>
                  <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />
                  <ComponentLibrary.PCLabel text={"DIMENSIONS"} letterSpacing={"1px"} />
                  <div className="f4 fw6 dark-gray mt1">
                    {inspection.width} × {inspection.height} px
                  </div>
                  <div className="f7 mid-gray mt1">Read from the ad.size meta tag in index.html.</div>
                </>
              )}

              {inspection.files.length > 0 && (
                <>
                  <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />
                  <ComponentLibrary.PCDivider />
                  <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={3} />
                  <div className="flex items-center justify-between">
                    <div className="flex items-baseline">
                      <ComponentLibrary.PCLabel text={"CONTENTS"} letterSpacing={"1px"} />
                      <span className="ml2 f7 mid-gray">
                        {inspection.files.length} files · {formatBytes(inspection.totalBytes)}
                      </span>
                    </div>
                    <span className="pointer f6 fw5 color-text-5" onClick={() => setContentsOpen((o) => !o)}>
                      {contentsOpen ? "Collapse" : "Expand"}
                    </span>
                  </div>
                  {contentsOpen && (
                    <div className="mt2">
                      {inspection.files.map((f) => (
                        <div key={f.path} className="flex items-center justify-between pv2 ph1" style={{ borderBottom: "1px solid #f0f0f0" }}>
                          <div className="flex items-center" style={{ minWidth: 0 }}>
                            <ComponentLibrary.PCIconPicker icon={KIND_ICON[f.kind] as any} />
                            <span className="ml2 f6 dark-gray" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {f.path}
                            </span>
                            {f.isEntry && <span className="ml2 ph2 br2 f7 fw6" style={{ backgroundColor: "#eaf7ee", color: "#1e7e45" }}>ENTRY</span>}
                          </div>
                          <span className="f7 mid-gray ml3" style={{ whiteSpace: "nowrap" }}>
                            {formatBytes(f.size)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </ComponentLibrary.PCSpacer>
    </ComponentLibrary.PCContainerMain>
  );
}
